import { useAppSelector } from "app/hooks";
import { useAuth } from "hooks/auth";
import { querystring } from "models/Listing";
import { selectCurrentStoreData } from "reduxState/currentStoreData/currentStoreDataSlice";
import useSWR, { SWRResponse } from "swr";
import { ImportTypes } from "types/import";
import { ListingProps, ListResponse } from "types/listing";
import { PointTransferPayload, TransferSearchType } from "types/unit-transfers";
import { getEnvVariable } from "utilities/env";

export const usePoints = () => {
  const { code } = useAppSelector(selectCurrentStoreData);
  const { fetcher } = useAuth();

  const usePointsTransferList = (
    props?: ListingProps,
  ): SWRResponse<ListResponse<TransferSearchType>, any> => {
    return useSWR(
      () =>
        code
          ? `${getEnvVariable(
              "REACT_APP_API_URL",
            )}/${code}/points${querystring(props)}`
          : null,
      fetcher,
    );
  };

  const importUnitsTransfers = async (
    type: ImportTypes.UNIT_TRANSFER_ADDING | ImportTypes.UNIT_TRANSFER_SPENDING,
    file?: string | Blob,
  ): Promise<{ data: { importId: string } }> => {
    const formData = new FormData();
    file && formData.append("import[file]", file);
    const response = await fetcher(
      `${getEnvVariable("REACT_APP_API_URL")}/${code}/import/${type}`,
      { body: formData, method: "post" },
    );
    const data = await response.json();

    if (response.ok) {
      return { data };
    } else {
      throw data;
    }
  };

  const addPoints = async (
    payload: PointTransferPayload,
  ): Promise<{ data: { transferId: string } }> => {
    const response = await fetcher(
      `${getEnvVariable("REACT_APP_API_URL")}/${code}/points/add`,
      {
        body: JSON.stringify({ transfer: payload }),
        headers: { "Content-Type": "application/json" },
        method: "post",
      },
    );

    const data = await response.json();

    if (response.ok) {
      return { data };
    } else {
      throw data;
    }
  };

  const spendPoints = async (
    payload: PointTransferPayload,
  ): Promise<{ data: { transferId: string } }> => {
    const response = await fetcher(
      `${getEnvVariable("REACT_APP_API_URL")}/${code}/points/spend`,
      {
        body: JSON.stringify({ transfer: payload }),
        headers: { "Content-Type": "application/json" },
        method: "post",
      },
    );
    const data = await response.json();

    if (response.ok) {
      return { data };
    } else {
      throw data;
    }
  };

  const cancelPointsTransfer = async (transferID: string) => {
    const response = await fetcher(
      `${getEnvVariable(
        "REACT_APP_API_URL",
      )}/${code}/points/${transferID}/cancel`,
      { headers: { "Content-Type": "application/json" }, method: "post" },
    );
    if (response.status === 204) {
      return true;
    } else {
      throw await response.json();
    }
  };

  const activatePointsTransfer = async (transferID: string) => {
    const response = await fetcher(
      `${getEnvVariable(
        "REACT_APP_API_URL",
      )}/${code}/points/${transferID}/activate`,
      { headers: { "Content-Type": "application/json" }, method: "post" },
    );
    if (response.status === 204) {
      return true;
    } else {
      throw await response.json();
    }
  };

  const expirePointsTransfer = async (transferID: string) => {
    const response = await fetcher(
      `${getEnvVariable(
        "REACT_APP_API_URL",
      )}/${code}/points/${transferID}/expire`,
      { headers: { "Content-Type": "application/json" }, method: "post" },
    );
    if (response.status === 204) {
      return true;
    } else {
      throw await response.json();
    }
  };

  return {
    activatePointsTransfer,
    addPoints,
    cancelPointsTransfer,
    expirePointsTransfer,
    spendPoints,
    usePointsTransferList,
    importUnitsTransfers,
  };
};
